import { Actions, ofType, createEffect } from '@ngrx/effects';
import {
  ChageUserPassword,
  ClearUserFoto,
  EditUser,
  Error,
  LoadLocations,
  LoadLocationsSuccess,
  LoadMoreEventNotifications,
  LoadMoreEventNotificationsSuccess,
  LoadMoreMessageNotifications,
  LoadMoreMessageNotificationsSuccess,
  LoadNotificationCount,
  LoadNotificationCountSuccess,
  LoadUser,
  LoadUserEventNotifications,
  LoadUserEventNotificationsSuccess,
  LoadUserMessageNotifications,
  LoadUserMessageNotificationsSuccess,
  LoadUserSuccess,
  ReadAllNotifications,
  ReadAllNotificationsSuccess,
  ReadEventNotifications,
  ReadEventNotificationSuccess,
  ReadMessageNotification,
  ReadMessageNotificationSuccess,
  UploadUserFoto,
  UserActionsTypes,
} from './user.actions';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';

import { Action, Store } from '@ngrx/store';
import { Doctor } from '@nrwl/rx-models';
import { Injectable, inject } from '@angular/core';
import { SnackbarService } from '@nrwl/snackbar-services';
import { UserService } from '@nrwl/rx-service-user';
import * as action from '../active-clients/active-clients.actions';
import { UserState } from './user.state';
import { FeatureFlagsService, FeatureFlagsStore } from '@nrwl/rx-state-feature-flags';

@Injectable()
export class UserEffects {
  private actions$ = inject(Actions);
  private store = inject(Store<UserState>);
  private userService = inject(UserService);
  private snackbarService = inject(SnackbarService);
  private featureFlagsStore = inject(FeatureFlagsStore);
  private featureFlagsService = inject(FeatureFlagsService);

  loadClientTags$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType<LoadUser>(UserActionsTypes.LoadUser),
        mergeMap((action: any) =>
          this.userService.getUserProfile(action).pipe(
            map((res: any) => new LoadUserSuccess(new Doctor(res))),
            catchError((err: any) => of(new Error(err))),
          ),
        ),
      ),
  );

  LoadLocations$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType<LoadLocations>(UserActionsTypes.LoadLocations),
        mergeMap((action: any) =>
          this.userService.getLocations(action).pipe(
            map((res: any) => new LoadLocationsSuccess(res)),
            catchError((err: any) => of(new Error(err))),
          ),
        ),
      ),
  );

  EditUser$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType<EditUser>(UserActionsTypes.EditUser),
        mergeMap((action: any) =>
          this.userService.editUser(action).pipe(
            map((res: any) => {
              if (!action.payload.suppressNotification) {
                this.snackbarService.success('Changes were successfully saved');
              }
              return new LoadUser();
            }),
            catchError((err: any) => {
              this.snackbarService.error();
              return of(new Error(err));
            }),
          ),
        ),
      ),
  );

  UploadUserFoto$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType<UploadUserFoto>(UserActionsTypes.UploadUserFoto),
        mergeMap((action: any) =>
          this.userService.uploadUserFoto(action).pipe(
            map(() => {
              this.snackbarService.success('Changes were successfully saved');
              return new LoadUser();
            }),
            catchError((err: any) => {
              this.snackbarService.error();
              return of(new Error(err));
            }),
          ),
        ),
      ),
  );

  ClearUserFoto$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType<ClearUserFoto>(UserActionsTypes.ClearUserFoto),
        mergeMap((action: any) =>
          this.userService.removeUserFoto(action).pipe(
            map((res: any) => {
              this.snackbarService.success('Changes were successfully saved');
              return new LoadUser();
            }),
            catchError((err: any) => {
              this.snackbarService.error();
              return of(new Error(err));
            }),
          ),
        ),
      ),
  );

  ChageUserPassword$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType<ChageUserPassword>(UserActionsTypes.ChageUserPassword),
        mergeMap((action: any) =>
          this.userService.changeUserPassword(action).pipe(
            map((res: any) => {
              this.snackbarService.success('Changes were successfully saved');
              return new LoadUser();
            }),
            catchError((err: any) => {
              this.snackbarService.error('Incorrect current password');
              return of(new Error(err));
            }),
          ),
        ),
      ),
  );

  LoadUserEventNotifications$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType<LoadUserEventNotifications>(UserActionsTypes.LoadUserEventNotifications),
        mergeMap((item) => {
          return this.userService.getUserEventNotifications().pipe(
            map((items: any) => {
              return new LoadUserEventNotificationsSuccess(items);
            }),
            catchError((err: any) => {
              this.snackbarService.error();
              return of(new action.Error(err));
            }),
          );
        }),
      ),
  );

  LoadUserMessageNotifications$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType<LoadUserMessageNotifications>(UserActionsTypes.LoadUserMessageNotifications),
        mergeMap((item) => {
          return this.userService.getUserMessageNotifications().pipe(
            map((items: any) => {
              return new LoadUserMessageNotificationsSuccess(items);
            }),
            catchError((err: any) => {
              this.snackbarService.error();
              return of(new action.Error(err));
            }),
          );
        }),
      ),
  );

  LoadMoreNotification$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType<LoadMoreEventNotifications | LoadMoreMessageNotifications>(
          UserActionsTypes.LoadMoreMessageNotifications,
          UserActionsTypes.LoadMoreEventNotifications,
        ),
        mergeMap((action: any) => {
          return this.userService.getMoreNotifications(action.payload).pipe(
            map((res: any) => {
              if (action.type === UserActionsTypes.LoadMoreEventNotifications) {
                return new LoadMoreEventNotificationsSuccess(res);
              } else {
                return new LoadMoreMessageNotificationsSuccess(res);
              }
            }),
            catchError((err: any) => {
              this.snackbarService.error();
              return of(new Error(err));
            }),
          );
        }),
      ),
  );

  ReadNotifications$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType<ReadEventNotifications | ReadMessageNotification>(
          UserActionsTypes.ReadMessageNotification,
          UserActionsTypes.ReadEventNotifications,
        ),
        mergeMap((action: any) => {
          return this.userService.readBulkNotification(action.payload).pipe(
            map((res: any) => {
              this.snackbarService.success('Success!');
              if (action.type === UserActionsTypes.ReadEventNotifications) {
                return new ReadEventNotificationSuccess(action.payload);
              } else {
                return new ReadMessageNotificationSuccess(action.payload);
              }
            }),
            catchError((err: any) => {
              this.snackbarService.error();
              return of(new Error(err));
            }),
          );
        }),
      ),
  );

  ReadAllNotifications$ = createEffect(
    (): Observable<any> =>
      this.actions$.pipe(
        ofType<ReadAllNotifications>(UserActionsTypes.ReadAllNotifications),
        mergeMap((action: any) => {
          return this.userService.readAllNotification().pipe(
            map((res: any) => {
              this.snackbarService.success('Success!');
              return new ReadAllNotificationsSuccess();
            }),
            catchError((err: any) => {
              this.snackbarService.error();
              return of(new Error(err));
            }),
          );
        }),
      ),
  );

  LoadNotificationCount$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType<LoadNotificationCount>(UserActionsTypes.LoadNotificationCount),
        mergeMap((action: any) => {
          return this.userService.getNotificationAmount().pipe(
            map((res: any) => {
              return new LoadNotificationCountSuccess(res.message_count);
            }),
          );
        }),
      ),
  );

  syncFeatureFlagsOnUserLoad$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<LoadUserSuccess>(UserActionsTypes.LoadUserSuccess),
        tap((loadUserAction) => {
          const clinic = loadUserAction.payload?.clinic;
          const addons = clinic?.addons ?? clinic?.addon;
          this.featureFlagsStore.setFromAddons(addons);
        }),
        switchMap((loadUserAction) =>
          this.featureFlagsService.syncFromLaunchDarkly(loadUserAction.payload).pipe(
            catchError(() => {
              this.snackbarService.error();
              return EMPTY;
            }),
          ),
        ),
      ),
    { dispatch: false },
  );
}
